import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { match } from 'ts-pattern';

import { Button } from '@/components/button/button';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Input } from '@/components/input/input';
import Modal from '@/components/modal/modal';
import {
  ExampleTypeEnum,
  METRIC_TYPES,
  MetricConfigAPI,
  MetricConfigLLMScore,
  MetricProviderCreateReq,
  MetricTypeEnum
} from '@/types/evals';

import { ExampleTypesField } from './ExampleTypesField';
import { MetricApiConfigFields } from './MetricApiConfigFields';
import { MetricLlmConfigFields } from './MetricLlmConfigFields';
import styles from './modalMetricCreate.module.scss';
import {
  DESCRIPTION_MAX_LENGTH, MetricConfigFormData, MetricFormData, metricFormSchema, NAME_MAX_LENGTH
} from './schemas';

export type MetricModalMode = 'create' | 'clone' | 'version';

export type MetricModalInitial = {
  type: MetricTypeEnum;
  config: MetricConfigAPI | MetricConfigLLMScore;
  producesMetrics: string[];
  exampleType: ExampleTypeEnum[];
  name?: string;
  description?: string;
};

type Props = {
  cb: ( ret: MetricProviderCreateReq ) => void;
  onClose: () => void;
  isSubmitting?: boolean;
  mode?: MetricModalMode;
  initial?: MetricModalInitial;
  /** Next version number, used for the "Version N of …" title in version mode. */
  nextVersion?: number;
};

const TEMP_NONE = -0.1;

const METRIC_TYPE_OPTIONS: MetricTypeEnum[] = [ METRIC_TYPES.API_CLASSIFIER, METRIC_TYPES.LLM_AS_A_JUDGE ];

const TYPE_HEADINGS: Record<MetricTypeEnum, string> = {
  [ METRIC_TYPES.API_CLASSIFIER ]: 'Configure API Classifier Properties',
  [ METRIC_TYPES.LLM_AS_A_JUDGE ]: 'Configure LLM as a Judge Properties'
};

// Map the stored metric config into the ergonomic form shape (temperature as a
// number with the -0.1 "None" sentinel; prompt params as editable rows).
function toFormConfig(
  type: MetricTypeEnum | undefined,
  config: MetricConfigAPI | MetricConfigLLMScore | undefined
): MetricConfigFormData {
  if ( !type || !config ) return {};
  if ( type === METRIC_TYPES.API_CLASSIFIER ) {
    const c = config as MetricConfigAPI;
    return { url: c.url, headers: c.headers ?? [], isAsync: c.is_async ?? false };
  }
  const c = config as MetricConfigLLMScore;
  const tempStr = c.model_parameters?.temperature;
  const parsed = parseFloat( tempStr ?? 'NaN' );
  const temperature = tempStr === 'default' ? TEMP_NONE : ( Number.isNaN( parsed ) ? 0 : parsed );
  return {
    model: c.model_parameters?.model,
    prompt: c.prompt,
    temperature,
    promptParameters: Object.entries( c.prompt_parameters ?? {} ).map( ( [ key, value ] ) => ( { key, value } ) )
  };
}

function buildDefaultValues( mode: MetricModalMode, initial?: MetricModalInitial ): MetricFormData {
  const name = mode === 'clone' && initial?.name ? `${ initial.name } (CLONE)` : initial?.name ?? '';
  const description = mode === 'clone' && initial?.description
    ? `${ initial.description } (CLONE)`
    : initial?.description ?? '';
  return {
    name,
    description,
    // create starts unselected; clone/version lock to the source's type.
    metric_type: ( initial?.type ?? undefined ) as MetricTypeEnum,
    exampleType: initial?.exampleType ?? [],
    producesMetrics: ( initial?.producesMetrics ?? [] ).join( ',' ),
    config: toFormConfig( initial?.type, initial?.config )
  };
}

// Convert the form shape back into the domain configuration sent to the API.
function buildConfiguration( data: MetricFormData ): MetricConfigAPI | MetricConfigLLMScore {
  if ( data.metric_type === METRIC_TYPES.API_CLASSIFIER ) {
    return {
      url: data.config.url ?? '',
      headers: ( data.config.headers ?? [] ).filter( ( h ) => h.header.trim().length > 0 ),
      is_async: !!data.config.isAsync
    };
  }
  const params = ( data.config.promptParameters ?? [] ).filter( ( p ) => p.key.trim().length > 0 );
  const temp = data.config.temperature;
  return {
    prompt: data.config.prompt ?? '',
    model_parameters: {
      model: data.config.model ?? '',
      temperature: temp !== undefined && temp < 0 ? 'default' : ( temp?.toString() ?? '0' )
    },
    ...( params.length > 0 && {
      prompt_parameters: Object.fromEntries( params.map( ( p ) => [ p.key, p.value ] ) )
    } )
  };
}

export default function ModalMetricCreate( { cb, onClose, isSubmitting, mode = 'create', initial, nextVersion }: Props ) {
  const methods = useForm<MetricFormData>( {
    resolver: zodResolver( metricFormSchema ),
    defaultValues: buildDefaultValues( mode, initial )
  } );
  const { register, handleSubmit, watch, setValue, formState: { errors, isDirty } } = methods;

  const metricType = watch( 'metric_type' );
  const isVersion = mode === 'version';
  const isLocked = mode === 'clone' || mode === 'version';
  const versionGated = isVersion && !isDirty;

  const title = match( mode )
    .with( 'clone', () => 'Clone Metric' )
    .with( 'version', () => `Version ${ nextVersion ?? '' } of ${ initial?.name ?? 'Metric' }`.trim() )
    .otherwise( () => 'Create Metric' );

  const subtitle = mode === 'create'
    ? 'Create metrics that define how agents perform individually and together across real workflows.'
    : 'Metrics define how agents perform individually and together across real workflows.';

  const submitLabel = match( mode )
    .with( 'clone', () => ( isSubmitting ? 'Cloning...' : 'Clone Metric' ) )
    .with( 'version', () => ( isSubmitting ? 'Versioning...' : 'Version Metric' ) )
    .otherwise( () => ( isSubmitting ? 'Creating...' : 'Create Metric' ) );

  const onSubmit = ( data: MetricFormData ) => {
    cb( {
      name: data.name,
      description: data.description ?? '',
      example_type: data.exampleType,
      configuration: buildConfiguration( data ),
      produces_metrics: data.producesMetrics.split( ',' ).map( ( s ) => s.trim() ).filter( Boolean )
    } );
  };

  const renderConfig = () => match( metricType )
    .with( METRIC_TYPES.API_CLASSIFIER, () => <MetricApiConfigFields disabled={ isSubmitting } /> )
    .with( METRIC_TYPES.LLM_AS_A_JUDGE, () => <MetricLlmConfigFields disabled={ isSubmitting } /> )
    .otherwise( () => null );

  return (
    <Modal>
      <FormProvider { ...methods }>
        <form id="metric-config-form" onSubmit={ handleSubmit( onSubmit ) } className={ styles.modal_wrapper }>
          <div className={ styles.modal_header }>
            <h2 data-testid="modal-title">{title}</h2>
            <p className={ styles.modal_subtitle }>{subtitle}</p>
          </div>

          <div className={ styles.modal_content }>
            {!isVersion && (
              <div className={ clsx( 'layout__col', 'pad' ) }>
                <div className={ styles.label_row }>
                  <label className={ clsx( 'auth_flow_label', errors.name && 'required' ) } htmlFor="name">
                    Metric Name <span className={ styles.required_asterisk }>*</span>
                  </label>
                  <span className={ styles.char_limit }>Max { NAME_MAX_LENGTH } characters</span>
                </div>
                <Input
                  id="name"
                  type="text"
                  maxLength={ NAME_MAX_LENGTH }
                  placeholder={ 'Ex. "Topical Relevance"' }
                  data-testid="metric-name-input"
                  disabled={ isSubmitting }
                  error={ errors.name && ( errors.name.message || 'This field is required' ) }
                  { ...register( 'name' ) }
                />
              </div>
            )}

            <div className={ clsx( 'layout__col', 'pad' ) }>
              <div className={ styles.label_row }>
                <label className="auth_flow_label" htmlFor="description">Description (optional)</label>
                <span className={ styles.char_limit }>Max { DESCRIPTION_MAX_LENGTH } characters</span>
              </div>
              <textarea
                id="description"
                className={ styles.description_textarea }
                rows={ 3 }
                maxLength={ DESCRIPTION_MAX_LENGTH }
                placeholder="Optional description for this metric"
                data-testid="metric-description-input"
                disabled={ isSubmitting || isVersion }
                { ...register( 'description' ) }
              />
              {errors.description && (
                <div className={ styles.error_message } data-testid="metric-description-error">{errors.description.message}</div>
              )}
            </div>

            {mode === 'create' && (
              <div className={ clsx( 'layout__col', 'pad' ) } data-testid="metric-type-selection">
                <label className={ clsx( 'auth_flow_label', errors.metric_type && 'required' ) }>
                  Select Metric Type <span className={ styles.required_asterisk }>*</span>
                </label>
                <Menu
                  variant="select"
                  className={ clsx( styles.metric_type_select, !metricType && styles.metric_type_placeholder ) }
                  value={ metricType ?? 'Select Metric Type' }
                  data-testid="metric-type-dropdown"
                  disabled={ isSubmitting }
                >
                  {METRIC_TYPE_OPTIONS.map( ( type ) => (
                    <MenuItem
                      key={ type }
                      value={ type }
                      data-testid={ `metric-type-option-${ type }` }
                      onClick={ () => setValue( 'metric_type', type, { shouldValidate: true, shouldDirty: true } ) }
                    />
                  ) )}
                </Menu>
                {errors.metric_type && (
                  <div className={ styles.error_message }>{errors.metric_type.message || 'Please select a metric type'}</div>
                )}
              </div>
            )}

            {metricType && (
              <h3 className={ styles.config_section_title } data-testid="config-section-title">
                {TYPE_HEADINGS[ metricType ]}
              </h3>
            )}
            {renderConfig()}

            <div className={ clsx( 'layout__col', 'pad' ) }>
              <label className={ clsx( 'auth_flow_label', errors.producesMetrics && 'required' ) } htmlFor="producesMetrics">
                Produces Metrics <span className={ styles.required_asterisk }>*</span> (comma separated)
              </label>
              <Input
                id="producesMetrics"
                type="text"
                placeholder="Ex. accuracy, relevance"
                data-testid="produces-metrics-input"
                disabled={ isSubmitting }
                error={ errors.producesMetrics && ( errors.producesMetrics.message || 'This field is required' ) }
                { ...register( 'producesMetrics' ) }
              />
            </div>

            <ExampleTypesField disabled={ isSubmitting } />
          </div>

          <div className={ styles.modal_footer }>
            <div className={ styles.footer_status }>
              {versionGated && (
                <span className={ styles.footer_hint } data-testid="version-gate-hint">
                  Edit metric properties to add new version
                </span>
              )}
            </div>
            <div className={ clsx( 'layout__row', 'pad', 'flex_right', styles.modal_actions ) }>
              <Button variant="secondary" size="sm" onClick={ onClose } disabled={ isSubmitting } data-testid="metric-cancel-button">
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                loading={ isSubmitting }
                disabled={ isSubmitting || versionGated || ( isLocked && !metricType ) }
                data-testid="metric-submit-button"
              >
                {submitLabel}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
}
